import React, { useState } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PasswordInput } from '@/components/PasswordInput';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/api';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

export const DeleteAccount: React.FC = () => {
  const { t } = useTranslation(['account', 'common']);
  const { user, deleteAccount, logout } = useAuth();
  const navigate = useNavigate();
  const isTelegramOnly = !user?.email;

  const [showConfirm, setShowConfirm] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleCancel = () => {
    setShowConfirm(false);
    setPassword('');
    setConfirmText('');
  };

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isTelegramOnly) {
      if (confirmText !== 'УДАЛИТЬ') {
        toast.error(t('account:typeDeleteToConfirmError'));
        return;
      }
    } else {
      if (!password || password.length < 6) {
        toast.error(t('account:enterPasswordMinChars'));
        return;
      }
    }

    setIsLoading(true);
    try {
      if (isTelegramOnly) {
        await apiClient.delete('/auth/account', { data: { confirm_text: confirmText } });
        logout();
      } else {
        await deleteAccount(password);
      }
      navigate('/', { replace: true });
    } catch (err: any) {
      const message = err.response?.data?.message || t('account:errorDeletingAccount');
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const isConfirmDisabled = isTelegramOnly ? confirmText !== 'УДАЛИТЬ' : !password;

  return (
    <div className={classNames('card', styles.card)}>
      <div className="d-flex flex-column g-8">
        <h3 style={{ color: 'var(--red)' }}>{t('account:accountDeletion')}</h3>
        <p>
          {t('account:deletionWarning')}
        </p>
      </div>

      {!showConfirm ? (
        <div className={styles.actions}>
          <button
            data-testid="delete-account-btn"
            className="btn btn--sm btn--outlined"
            style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
            onClick={() => setShowConfirm(true)}
          >
            {t('account:deleteAccount')}
          </button>
        </div>
      ) : (
        <form className="d-flex flex-column g-40" onSubmit={handleDelete}>
          <div className="d-flex flex-column g-20">
            {isTelegramOnly ? (
              <input
                data-testid="delete-confirm-text"
                type="text"
                className="field"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={t('account:typeDeleteToConfirm')}
                disabled={isLoading}
                autoFocus
              />
            ) : (
              <PasswordInput
                data-testid="delete-confirm-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('account:enterPasswordToConfirm')}
                minLength={6}
                disabled={isLoading}
                autoFocus
              />
            )}
          </div>
          <div className={styles.actions}>
            <button
              data-testid="delete-cancel-btn"
              className="btn btn--sm btn--outlined"
              type="button"
              onClick={handleCancel}
              disabled={isLoading}
            >
              {t('common:cancel')}
            </button>
            <button
              data-testid="delete-confirm-btn"
              className="btn btn--sm"
              style={{ backgroundColor: 'var(--red)', color: '#fff' }}
              type="submit"
              disabled={isLoading || isConfirmDisabled}
            >
              {isLoading ? t('account:deleting') : t('account:confirmDeletion')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default DeleteAccount;
